import { howToTexts, HOW_TO_SCALE } from "./howTo";
import { pointsTextLang, type PointsTextLang } from "./pointsActions";
import { formatPoints, type SphereKey } from "./spheres";

/**
 * «Ваши баллы» в окне «за что эти баллы»: сколько у человека в сфере — ровно как на
 * карточке сферы экрана «Как получить баллы» («15 050 из 300 000»: те же счёт и шкала; число —
 * с разделителем языка страницы, «15,050 of 300,000», как в профиле) —
 * и следующий уровень сферы (lib/mineLevels.ts), у высшего — что он высший.
 *
 * «Тренировки» — как строка профиля и уровень: недели по плану («У вас 1 неделя по плану»), а
 * баллы — отдельной простой строкой («16 250 баллов»), без «из 100 000» (владелец 10.10). Недель
 * не знаем (ответа достижений нет) — только баллы, тоже без «из».
 */

type MineTexts = {
    /** «У вас» — перед числом. */
    have: string;
    /** «Это высший уровень — «Хранитель проекта»». */
    top: (name: string) => string;
};

const TEXTS: Record<PointsTextLang, MineTexts> = {
    ru: { have: "У вас", top: (name) => `Это высший уровень — «${name}»` },
    en: { have: "You have", top: (name) => `This is the top level — “${name}”` },
    de: { have: "Sie haben", top: (name) => `Das ist das höchste Level — „${name}“` },
    es: { have: "Tienes", top: (name) => `Es el nivel más alto: «${name}»` },
    cn: { have: "您已有", top: (name) => `已是最高等级「${name}」` },
    ar: { have: "لديك", top: (name) => `هذا أعلى مستوى — «${name}»` },
};

export const mineTexts = (lang: string | null | undefined): MineTexts => TEXTS[pointsTextLang(lang)];

/** Уровень, как его отдаёт lib/mineLevels (без каталога в этом модуле). */
export type MineLevelInfo = { top: boolean; n: number; name: string; img?: string; line?: string };

export type MineContent = {
    sphere: SphereKey;
    /** «У вас» */
    have: string;
    /** «15 050» — как на карточке сферы. */
    value: string;
    /** «из 300 000»; у «Тренировок» — «неделя по плану» (или «баллов», когда недель не знаем). */
    of: string;
    /** «Тренировки» по неделям: баллы простой строкой «16 250 баллов»; иначе null. */
    points: string | null;
    /** Строка уровня: «Ещё … — и вы получите уровень «…»» или «Это высший уровень — «…»». */
    level: string | null;
    /** Картинка следующего (у высшего — его) уровня. */
    img: string | null;
    top: boolean;
};

/** Что показать в окне: счёт сферы, шкала и строка уровня. value null — не показываем ничего. */
export const mineContent = (
    sphere: SphereKey,
    value: number | null | undefined,
    level: MineLevelInfo | null,
    lang: string | null | undefined,
    /** «Тренировки»: недели по плану из ответа достижений (ladders.workouts.value); нет — undefined. */
    weeks?: number,
): MineContent | null => {
    if (value == null || !Number.isFinite(value)) return null;
    const tx = mineTexts(lang);
    const how = howToTexts(pointsTextLang(lang));
    const line = level ? (level.top ? tx.top(level.name) : level.line || null) : null;
    const byWeeks = sphere === "workouts" && typeof weeks === "number" && Number.isFinite(weeks);
    const head =
        sphere !== "workouts"
            ? { value: formatPoints(value, lang), of: how.of(formatPoints(HOW_TO_SCALE[sphere], lang)), points: null }
            : byWeeks
              ? {
                    value: formatPoints(weeks as number, lang),
                    of: how.unitWeeks(weeks as number),
                    points: `${formatPoints(value, lang)} ${how.unitPoints(value)}`,
                }
              : { value: formatPoints(value, lang), of: how.unitPoints(value), points: null };
    return {
        sphere,
        have: tx.have,
        ...head,
        level: line,
        img: line && level?.img ? level.img : null,
        top: !!level?.top,
    };
};
